import type { LabResultCategoryGroup, LabResultCategoryKey } from '@liza/fhir-utils';
import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLabResultCategoryLabel } from '../../hooks/useLabResultCategoryLabel';
import { cn } from '../../utils/cn';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { LabCategoryMeasurements } from './LabCategoryMeasurements';
import { LabResultCategorySummary } from './LabResultCategorySummary';

export type LabResultCategoryListProps = {
  categories: LabResultCategoryGroup[];
  /** The category whose level-2 view is open, so its header reads as current. */
  selectedCategory?: LabResultCategoryKey;
  onSelectCategory: (category: LabResultCategoryKey) => void;
  /** Fired with a row's result code — the single-lab detail. */
  onSelectLab?: (code: string) => void;
  /**
   * Whether categories start expanded. Collapsed by default: the condensed line per category is the
   * view for scanning a whole chart, and a category is one click from its rows.
   */
  defaultExpanded?: boolean;
  /** Reference "today" for the collapsed lines' relative ages; injectable for stories. */
  now?: Date;
};

/**
 * The rows animate their own height open and shut, the same way the chat's sources panel does:
 * `tw-animate-css` keyframes interpolate to the `--radix-collapsible-content-height` Radix measures
 * onto the content, and `overflow-hidden` clips to the height in flight.
 */
const ROWS_PANEL_CLASS = cn(
  'overflow-hidden pt-1 pl-9',
  'data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
  'motion-reduce:animate-none',
);

/**
 * The category name is set as a section heading — small caps, tracked, muted — so it reads as the
 * container for the rows beneath it, whose names are sentence-case `text-sm` in the foreground color.
 * `leading-5` keeps it on the summary line's `text-sm` baseline while collapsed.
 */
const CATEGORY_LABEL_CLASS = cn(
  'w-40 shrink-0 text-xs/5 font-semibold tracking-wider text-muted-foreground uppercase',
  'transition-colors group-hover:text-foreground group-aria-current:text-foreground',
);

/**
 * The condensed line's slot collapses to nothing as the rows open, rather than unmounting and letting
 * the header snap shorter. A grid row is the one CSS height that transitions from `auto`: `1fr` to
 * `0fr` with the child clipped by `overflow-hidden`.
 */
const SUMMARY_SLOT_CLASS = cn(
  'grid min-w-0 flex-1 transition-[grid-template-rows,opacity] duration-200 ease-out',
  'motion-reduce:transition-none',
);

/**
 * Level 1 of the labs view: every curated category, collapsible. Collapsed, a category is one line
 * (or one per draw date) of `abbreviation value` pairs with the date and age of the draw; expanded, it
 * is one care-plan Results row per lab — name, sparkline, latest value with its unit and date — with
 * a lab that has never been drawn rendering as an empty row so a gap in the workup is visible. The
 * chevron toggles the rows; the rest of the header — name and condensed line — opens its level-2 charts.
 */
export const LabResultCategoryList = ({
  categories,
  selectedCategory,
  onSelectCategory,
  onSelectLab,
  defaultExpanded = false,
  now,
}: LabResultCategoryListProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const labelFor = useLabResultCategoryLabel();

  // The "all" toggle sets a baseline every category follows until it is toggled on its own; toggling
  // all again clears those overrides. Kept as baseline + overrides (not a set of open keys) so a
  // category that arrives after the baseline was set — `other`, once an uncurated result lands —
  // follows it too.
  const [baselineExpanded, setBaselineExpanded] = useState(defaultExpanded);
  const [overrides, setOverrides] = useState<ReadonlyMap<LabResultCategoryKey, boolean>>(new Map());
  const isExpanded = (key: LabResultCategoryKey): boolean => overrides.get(key) ?? baselineExpanded;
  const setExpanded = (key: LabResultCategoryKey, expanded: boolean): void =>
    setOverrides((prev) => new Map(prev).set(key, expanded));

  const anyExpanded = categories.some((category) => isExpanded(category.key));
  const toggleAll = (): void => {
    setBaselineExpanded(!anyExpanded);
    setOverrides(new Map());
  };

  // Unfiltered, the list always has its curated categories, so an empty list means a search or filter
  // matched nothing.
  if (categories.length === 0) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="lab-categories-empty">
          {t('noLabResultsFound')}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-2">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleAll}
          className="h-7 px-2 text-xs text-muted-foreground"
          data-testid="lab-categories-toggle-all"
        >
          {anyExpanded ? <ChevronsDownUp /> : <ChevronsUpDown />}
          {anyExpanded ? t('labCategoriesCollapseAll') : t('labCategoriesExpandAll')}
        </Button>
      </div>
      {categories.map((category) => {
        const expanded = isExpanded(category.key);
        const label = labelFor(category.key);
        const toggleLabel = expanded
          ? t('labCategoryCollapse', { category: label })
          : t('labCategoryExpand', { category: label });
        return (
          // Named by its category label so each one is a landmark a screen reader can jump between.
          <Collapsible
            key={category.key}
            role="region"
            aria-labelledby={`lab-category-${category.key}`}
            open={expanded}
            onOpenChange={(open) => setExpanded(category.key, open)}
            className="flex flex-col border-b border-border/60 pb-2 last:border-b-0"
          >
            <div className="flex items-start gap-1">
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="mt-0.5 shrink-0 text-muted-foreground"
                  aria-label={toggleLabel}
                  data-testid={`lab-category-toggle-${category.key}`}
                >
                  <ChevronDown
                    className={cn(
                      'transition-transform duration-200 motion-reduce:transition-none',
                      !expanded && '-rotate-90',
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onSelectCategory(category.key)}
                aria-current={selectedCategory === category.key ? 'true' : undefined}
                className={cn(
                  'group flex h-auto min-w-0 flex-1 items-start justify-start gap-1 p-1 text-left text-sm font-normal whitespace-normal',
                  selectedCategory === category.key && 'bg-accent/50',
                )}
                data-testid={`lab-category-${category.key}`}
              >
                <span id={`lab-category-${category.key}`} className={CATEGORY_LABEL_CLASS}>
                  {label}
                </span>
                <span
                  className={cn(SUMMARY_SLOT_CLASS, expanded ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr]')}
                  aria-hidden={expanded || undefined}
                >
                  <span
                    className="block min-h-0 overflow-hidden"
                    data-testid={expanded ? undefined : `lab-category-summary-${category.key}`}
                  >
                    <LabResultCategorySummary category={category} now={now} className="w-full" />
                  </span>
                </span>
              </Button>
            </div>
            <CollapsibleContent className={ROWS_PANEL_CLASS}>
              <LabCategoryMeasurements category={category} heading={null} onResultClick={onSelectLab} flagAbnormal />
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
};
